import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import api from "@/lib/api";
import { Loading, Crumbs, ShareMenu } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  ["problem", "Problema"], ["evidence", "Evidencia"], ["objective", "Objetivo"],
  ["solution", "Solución propuesta"], ["estimated_cost", "Costo estimado"],
  ["funding_source", "Fuente de financiamiento"], ["timeline_text", "Calendario"],
  ["responsible", "Responsables"], ["success_metrics", "Métricas de éxito"],
  ["risks", "Riesgos"], ["international_comparison", "Comparación internacional"],
  ["expert_opinion", "Opinión de expertos"],
];

export default function ProposalDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user, login } = useAuth();

  const load = useCallback(() => api.get(`/proposals/${id}`).then((r) => setP(r.data)).catch(() => setP(false)), [id]);
  useEffect(() => { load(); }, [load]);

  if (p === null) return <Loading />;
  if (p === false) return <div className="max-w-4xl mx-auto px-6 py-20 text-center text-slate-400">Propuesta no encontrada.</div>;

  const toggleSupport = async () => {
    if (!user) { toast.info("Crea tu cuenta para apoyar propuestas."); login(); return; }
    setBusy(true);
    try {
      const r = await api.post(`/proposals/${id}/support`);
      setP({ ...p, supports: r.data.supports, user_supported: r.data.supported });
      toast.success(r.data.supported ? "¡Gracias por tu apoyo!" : "Apoyo retirado.");
    } catch (e) { toast.error("No se pudo procesar."); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="proposal-detail">
      <Crumbs items={[{ label: "Propuestas", to: "/propuestas" }, { label: p.title }]} />
      <div className="flex justify-end -mt-2 mb-2"><ShareMenu title={p.title} /></div>
      {p.theme && <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">{p.theme}</span>}
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mt-2 mb-6">{p.title}</h1>

      <div className="bg-white rounded-lg border border-slate-200 p-6 flex items-center justify-between mb-8">
        <div>
          <p className="font-heading font-black text-3xl text-rose-500">{p.supports}</p>
          <p className="text-sm text-slate-500">ciudadanos apoyan esta propuesta</p>
        </div>
        <button onClick={toggleSupport} disabled={busy} data-testid="support-button"
          className={`inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-full transition-colors ${p.user_supported ? "bg-rose-500 text-white hover:bg-rose-600" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
          <Heart className={`w-4 h-4 ${p.user_supported ? "fill-current" : ""}`} /> {p.user_supported ? "Apoyando" : "Apoyar"}
        </button>
      </div>

      <div className="space-y-6">
        {FIELDS.map(([key, label]) => p[key] && (
          <div key={key} className="border-l-2 border-slate-200 pl-5">
            <p className="text-xs uppercase tracking-wide text-cyan-600 font-semibold mb-1">{label}</p>
            <p className="text-slate-700 leading-relaxed">{p[key]}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
